import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumMetricType } from "./enums/enumMetricType.generated";

// Model Objects

export class AssemblyQcTestMetricDefinition extends ModelObject<AssemblyQcTestMetricDefinition> {

    public id: Guid = Guid.createEmpty();
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * The type of metric being captured. Giving a metric a type allows the system to assign probe values straight into assemblies.
    */
    public type: EnumMetricType = EnumMetricType.custom;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.type = EnumMetricType.getByValue(jsonSerializedObject.type, this.type);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.type = this.type.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyQcTestMetricDefinition.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class AssemblyQcTestMetricLineReadOnly extends ModelObject<AssemblyQcTestMetricLineReadOnly> {

    /**
    * Assembly qc test to which this metric belongs.
    */
    public assemblyQcTestId: Guid = Guid.createEmpty();
    /**
    * Default ordering sequence value.
    */
    public sequence: number = 0;
    /**
    * Details of the base QC test metric.
    */
    public qcTestMetric: AssemblyQcTestMetricDefinition = new AssemblyQcTestMetricDefinition();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyQcTestId = Guid.parseOrEmpty(jsonSerializedObject.assemblyQcTestId);
          this.sequence = jsonSerializedObject.sequence;
          this.qcTestMetric = new AssemblyQcTestMetricDefinition(jsonSerializedObject.qcTestMetric);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyQcTestId = this.assemblyQcTestId.toString();
        toRet.sequence = this.sequence;
        toRet.qcTestMetric = this.qcTestMetric.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyQcTestId", rule: (v: any) => !v.isEmpty() || "AssemblyQcTestMetricLineReadOnly.AssemblyQcTestId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "qcTestMetric", rule: (v: any) => !!v || "qcTestMetric is required"},
            {field: "qcTestMetric", rule: (v: any) => v.validate()},
    ];
}


export class AssemblyQcTestMetricLine extends ModelObject<AssemblyQcTestMetricLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespace.
    */
    public notes: string = "";
    /**
    * Formula for calculating this metric from the measurements of other metrics. If formula is populated then this metric is expected to be calculated not measured.  Format of this formula is yet to be defined.
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespace.
    */
    public formula: string = "";
    /**
    * Is this metric shown on the results graph?
    */
    public isGraphable: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * The suffix to display as measurement units.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public unitSuffix: string = "";
    private _maximumDecimalPlaces: number | null = null;
    /**
    * The maximum number of decimal places allowed to be given for a measurement.
    */
    public get maximumDecimalPlaces(): number | null { return this._maximumDecimalPlaces }
    public set maximumDecimalPlaces(value: number | null) {
      this._maximumDecimalPlaces = value === undefined ? null : value;
    }
    private _maximumPermittedValue: number | null = null;
    /**
    * If provided, gives the maximum permitted value of the measurement, allowing basic validation of user entries.
    */
    public get maximumPermittedValue(): number | null { return this._maximumPermittedValue }
    public set maximumPermittedValue(value: number | null) {
      this._maximumPermittedValue = value === undefined ? null : value;
    }
    private _minimumPermittedValue: number | null = null;
    /**
    * If provided, gives the minimum permitted value of the measurement, allowing basic validation of user entries.
    */
    public get minimumPermittedValue(): number | null { return this._minimumPermittedValue }
    public set minimumPermittedValue(value: number | null) {
      this._minimumPermittedValue = value === undefined ? null : value;
    }
    private _expectedEndValue: number | null = null;
    /**
    * The value the metric is expected to have at the end of testing within an assembly.
    */
    public get expectedEndValue(): number | null { return this._expectedEndValue }
    public set expectedEndValue(value: number | null) {
      this._expectedEndValue = value === undefined ? null : value;
    }
    private _expectedStartValue: number | null = null;
    /**
    * The value the metric is expected to have at the start of testing within an assembly.
    */
    public get expectedStartValue(): number | null { return this._expectedStartValue }
    public set expectedStartValue(value: number | null) {
      this._expectedStartValue = value === undefined ? null : value;
    }
    private _maximumExpectedValue: number | null = null;
    /**
    * The maximum value the metric is expected to have during an assembly.
    */
    public get maximumExpectedValue(): number | null { return this._maximumExpectedValue }
    public set maximumExpectedValue(value: number | null) {
      this._maximumExpectedValue = value === undefined ? null : value;
    }
    private _minimumExpectedValue: number | null = null;
    /**
    * The minimum value the metric is expected to have during an assembly.
    */
    public get minimumExpectedValue(): number | null { return this._minimumExpectedValue }
    public set minimumExpectedValue(value: number | null) {
      this._minimumExpectedValue = value === undefined ? null : value;
    }
    /**
    * Indicates whether process actions should be restricted if the latest QC test result fall outside the expected range.
    */
    public isProcessRestricted: boolean = false;
    private _expectedBooleanValue: boolean | null = null;
    /**
    * For boolean metrics, indicates whether the result is expected to be True or False.
    */
    public get expectedBooleanValue(): boolean | null { return this._expectedBooleanValue }
    public set expectedBooleanValue(value: boolean | null) {
      this._expectedBooleanValue = value === undefined ? null : value;
    }
    public readOnly: AssemblyQcTestMetricLineReadOnly = new AssemblyQcTestMetricLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.notes = jsonSerializedObject.notes;
          this.formula = jsonSerializedObject.formula;
          this.isGraphable = jsonSerializedObject.isGraphable;
          this.name = jsonSerializedObject.name;
          this.unitSuffix = jsonSerializedObject.unitSuffix;
          this.maximumDecimalPlaces = jsonSerializedObject.maximumDecimalPlaces == null ? null : jsonSerializedObject.maximumDecimalPlaces;
          this.maximumPermittedValue = jsonSerializedObject.maximumPermittedValue == null ? null : jsonSerializedObject.maximumPermittedValue;
          this.minimumPermittedValue = jsonSerializedObject.minimumPermittedValue == null ? null : jsonSerializedObject.minimumPermittedValue;
          this.expectedEndValue = jsonSerializedObject.expectedEndValue == null ? null : jsonSerializedObject.expectedEndValue;
          this.expectedStartValue = jsonSerializedObject.expectedStartValue == null ? null : jsonSerializedObject.expectedStartValue;
          this.maximumExpectedValue = jsonSerializedObject.maximumExpectedValue == null ? null : jsonSerializedObject.maximumExpectedValue;
          this.minimumExpectedValue = jsonSerializedObject.minimumExpectedValue == null ? null : jsonSerializedObject.minimumExpectedValue;
          this.isProcessRestricted = jsonSerializedObject.isProcessRestricted;
          this.expectedBooleanValue = jsonSerializedObject.expectedBooleanValue == null ? null : jsonSerializedObject.expectedBooleanValue;
          this.readOnly = new AssemblyQcTestMetricLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.notes = this.notes;
        toRet.formula = this.formula;
        toRet.isGraphable = this.isGraphable;
        toRet.name = this.name;
        toRet.unitSuffix = this.unitSuffix;
        toRet.maximumDecimalPlaces = this.maximumDecimalPlaces == null ? null : this.maximumDecimalPlaces;
        toRet.maximumPermittedValue = this.maximumPermittedValue == null ? null : this.maximumPermittedValue;
        toRet.minimumPermittedValue = this.minimumPermittedValue == null ? null : this.minimumPermittedValue;
        toRet.expectedEndValue = this.expectedEndValue == null ? null : this.expectedEndValue;
        toRet.expectedStartValue = this.expectedStartValue == null ? null : this.expectedStartValue;
        toRet.maximumExpectedValue = this.maximumExpectedValue == null ? null : this.maximumExpectedValue;
        toRet.minimumExpectedValue = this.minimumExpectedValue == null ? null : this.minimumExpectedValue;
        toRet.isProcessRestricted = this.isProcessRestricted;
        toRet.expectedBooleanValue = this.expectedBooleanValue == null ? null : this.expectedBooleanValue;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyQcTestMetricLine.Id cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "formula", rule: (v: any) => v == null || v.length <= 100000 || "Formula has too many characters (100000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "unitSuffix", rule: (v: any) => v == null || v.length <= 1000 || "UnitSuffix has too many characters (1000)"},
            {field: "maximumDecimalPlaces", rule: (v: any) => v == null || v >= -2147483648 || "MaximumDecimalPlaces is not greater than minimum allowed value: -2147483648"},
            {field: "maximumDecimalPlaces", rule: (v: any) => v == null || v <= 2147483647 || "MaximumDecimalPlaces is above maximum allowed value: 2147483647"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyQcTestHeaderReadOnly extends ModelObject<AssemblyQcTestHeaderReadOnly> {

    /**
    * Default ordering sequence value.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class AssemblyQcTestHeader extends ModelObject<AssemblyQcTestHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _assemblyId: Guid | null = null;
    /**
    * Assembly which has this qc test. Qc test should be either have AssemblyId or ProcessId, but not both.
    */
    public get assemblyId(): Guid | null { return this._assemblyId }
    public set assemblyId(value: Guid | null) {
      this._assemblyId = value === undefined ? null : value;
    }
    private _processId: Guid | null = null;
    /**
    * Process which has this qc test.Qc test should be either have AssemblyId or ProcessId, but not both.
    */
    public get processId(): Guid | null { return this._processId }
    public set processId(value: Guid | null) {
      this._processId = value === undefined ? null : value;
    }
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespace.
    */
    public notes: string = "";
    /**
    * Test definition which was used. This is for future correlation (knowing the test came from that definition), not for obtaining test parameters as the values within the QcTestHeader will be copied to the AssemblyQcTestHeader at time of creation.
    */
    public qcTestId: Guid = Guid.createEmpty();
    public readOnly: AssemblyQcTestHeaderReadOnly = new AssemblyQcTestHeaderReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.assemblyId = jsonSerializedObject.assemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.processId = jsonSerializedObject.processId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.qcTestId = Guid.parseOrEmpty(jsonSerializedObject.qcTestId);
          this.readOnly = new AssemblyQcTestHeaderReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.assemblyId = this.assemblyId == null ? null : this.assemblyId.toString();
        toRet.processId = this.processId == null ? null : this.processId.toString();
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.qcTestId = this.qcTestId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyQcTestHeader.Id cannot be empty"},
            {field: "assemblyId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyQcTestHeader.AssemblyId cannot be empty"},
            {field: "processId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyQcTestHeader.ProcessId cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "qcTestId", rule: (v: any) => !v.isEmpty() || "AssemblyQcTestHeader.QcTestId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyQcTest extends ModelObject<AssemblyQcTest> {

    public header: AssemblyQcTestHeader = new AssemblyQcTestHeader();
    public metricLines: (AssemblyQcTestMetricLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new AssemblyQcTestHeader(jsonSerializedObject.header);
          this.metricLines = jsonSerializedObject.metricLines.map((v: any) => new AssemblyQcTestMetricLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.metricLines = this.metricLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "metricLines", rule: (v: any) => !!v || "metricLines is required"},
            {field: "metricLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyQcTestMetricLine[]).reduce((acc: Array<string>, curr: AssemblyQcTestMetricLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class AssemblyTestMetricLineUpdate extends ModelObject<AssemblyTestMetricLineUpdate> {

    public id: Guid = Guid.createEmpty();
    private _expectedEndValue: number | null = null;
    /**
    * The value the metric is expected to have at the end of testing within an assembly.
    */
    public get expectedEndValue(): number | null { return this._expectedEndValue }
    public set expectedEndValue(value: number | null) {
      this._expectedEndValue = value === undefined ? null : value;
    }
    private _expectedStartValue: number | null = null;
    /**
    * The value the metric is expected to have at the start of testing within an assembly.
    */
    public get expectedStartValue(): number | null { return this._expectedStartValue }
    public set expectedStartValue(value: number | null) {
      this._expectedStartValue = value === undefined ? null : value;
    }
    private _maximumExpectedValue: number | null = null;
    /**
    * The maximum value the metric is expected to have during an assembly.
    */
    public get maximumExpectedValue(): number | null { return this._maximumExpectedValue }
    public set maximumExpectedValue(value: number | null) {
      this._maximumExpectedValue = value === undefined ? null : value;
    }
    private _minimumExpectedValue: number | null = null;
    /**
    * The minimum value the metric is expected to have during an assembly.
    */
    public get minimumExpectedValue(): number | null { return this._minimumExpectedValue }
    public set minimumExpectedValue(value: number | null) {
      this._minimumExpectedValue = value === undefined ? null : value;
    }
    /**
    * Indicates whether process actions should be restricted if the latest QC test result fall outside the expected range.
    */
    public isProcessRestricted: boolean = false;
    private _expectedBooleanValue: boolean | null = null;
    /**
    * For boolean metrics, indicates whether the result is expected to be True or False.
    */
    public get expectedBooleanValue(): boolean | null { return this._expectedBooleanValue }
    public set expectedBooleanValue(value: boolean | null) {
      this._expectedBooleanValue = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.expectedEndValue = jsonSerializedObject.expectedEndValue == null ? null : jsonSerializedObject.expectedEndValue;
          this.expectedStartValue = jsonSerializedObject.expectedStartValue == null ? null : jsonSerializedObject.expectedStartValue;
          this.maximumExpectedValue = jsonSerializedObject.maximumExpectedValue == null ? null : jsonSerializedObject.maximumExpectedValue;
          this.minimumExpectedValue = jsonSerializedObject.minimumExpectedValue == null ? null : jsonSerializedObject.minimumExpectedValue;
          this.isProcessRestricted = jsonSerializedObject.isProcessRestricted;
          this.expectedBooleanValue = jsonSerializedObject.expectedBooleanValue == null ? null : jsonSerializedObject.expectedBooleanValue;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.expectedEndValue = this.expectedEndValue == null ? null : this.expectedEndValue;
        toRet.expectedStartValue = this.expectedStartValue == null ? null : this.expectedStartValue;
        toRet.maximumExpectedValue = this.maximumExpectedValue == null ? null : this.maximumExpectedValue;
        toRet.minimumExpectedValue = this.minimumExpectedValue == null ? null : this.minimumExpectedValue;
        toRet.isProcessRestricted = this.isProcessRestricted;
        toRet.expectedBooleanValue = this.expectedBooleanValue == null ? null : this.expectedBooleanValue;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyTestMetricLineUpdate.Id cannot be empty"},
    ];
}


export class AssemblyQcTestService extends ServiceBase {

  public async getAssemblyQcTest(assemblyQcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyQcTest> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcTestId:assemblyQcTestId.toString(),
    };
    const extractor = (response:any) => new AssemblyQcTest(response.assemblyQcTest);;
    return this.makeJsonRequest("assemblyQcTest/v1/getAssemblyQcTest", req, false, extractor, opts);
  };

  public async getAssemblyQcTestsForAssembly(assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = (response:any) => response.assemblyQcTests.map((v: any) => new AssemblyQcTest(v));;
    return this.makeJsonRequest("assemblyQcTest/v1/getAssemblyQcTestsForAssembly", req, false, extractor, opts);
  };

  public async addQcTestToAssembly(assemblyId: Guid, qcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyQcTest> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyId:assemblyId.toString(),
    qcTestId:qcTestId.toString(),
    };
    const extractor = (response:any) => new AssemblyQcTest(response.assemblyQcTest);;
    return this.makeJsonRequest("assemblyQcTest/v1/addQcTestToAssembly", req, true, extractor, opts);
  };

  public async updateAssemblyQcTestMetricLines(assemblyQcTestId: Guid, updates: readonly AssemblyTestMetricLineUpdate[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcTestId:assemblyQcTestId.toString(),
    updates:updates.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("assemblyQcTest/v1/updateAssemblyQcTestMetricLines", req, true, extractor, opts);
  };

  public async setAssemblyQcTestSequences(assemblyId: Guid, assemblyQcTestIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyId:assemblyId.toString(),
    assemblyQcTestIds:assemblyQcTestIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("assemblyQcTest/v1/setAssemblyQcTestSequences", req, true, extractor, opts);
  };

  public async hardDeleteAssemblyQcTest(assemblyQcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcTestId:assemblyQcTestId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("assemblyQcTest/v1/hardDeleteAssemblyQcTest", req, true, extractor, opts);
  };

  public async getAssemblyQcTestsForProcess(processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = (response:any) => response.assemblyQcTests.map((v: any) => new AssemblyQcTest(v));;
    return this.makeJsonRequest("assemblyQcTest/v1/getAssemblyQcTestsForProcess", req, false, extractor, opts);
  };

  public async addQcTestToProcess(processId: Guid, qcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyQcTest> {
    const req = {
    tenantId:api.tenantId.toString(),
    processId:processId.toString(),
    qcTestId:qcTestId.toString(),
    };
    const extractor = (response:any) => new AssemblyQcTest(response.assemblyQcTest);;
    return this.makeJsonRequest("assemblyQcTest/v1/addQcTestToProcess", req, true, extractor, opts);
  };

  public async setAssemblyQcTestSequencesForProcess(processId: Guid, assemblyQcTestIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    processId:processId.toString(),
    assemblyQcTestIds:assemblyQcTestIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("assemblyQcTest/v1/setAssemblyQcTestSequencesForProcess", req, true, extractor, opts);
  };

  public async getAssemblyQcTestsForBatch(batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = (response:any) => response.assemblyQcTests.map((v: any) => new AssemblyQcTest(v));;
    return this.makeJsonRequest("assemblyQcTest/v1/getAssemblyQcTestsForBatch", req, false, extractor, opts);
  };

  public async getAssemblyQcTestsForAssemblies(assemblyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyIds:assemblyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assemblyQcTests.map((v: any) => new AssemblyQcTest(v));;
    return this.makeJsonRequest("assemblyQcTest/v1/getAssemblyQcTestsForAssemblies", req, false, extractor, opts);
  };

  public async hardDeleteAssemblyQcTests(assemblyQcTestIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcTestIds:assemblyQcTestIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("assemblyQcTest/v1/hardDeleteAssemblyQcTests", req, true, extractor, opts);
  };

  public async getAssemblyQcTestsForProcesses(processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assemblyQcTests.map((v: any) => new AssemblyQcTest(v));;
    return this.makeJsonRequest("assemblyQcTest/v1/getAssemblyQcTestsForProcesses", req, false, extractor, opts);
  };
}

export const assemblyQcTestService = new AssemblyQcTestService();